import { openUrlInNewWindow } from '@/utils'

// Plain-JSON shape used to pass comment documents through Inertia forms without
// dragging the recursive IssueCommentDocument type into useForm's generics.
export type JsonValue = string | number | boolean | null | JsonValue[] | JsonObject
export type JsonObject = { [key: string]: JsonValue }

export const issueStatuses = [
  'reported',
  'acknowledged',
  'in_progress',
  'partially_resolved',
  'resolved',
  'rejected',
  'duplicate',
  'forwarded',
] as const satisfies readonly App.Enums.IssueStatus[]

export const issueStatusLabels: Record<App.Enums.IssueStatus, string> = {
  reported: 'Gemeldet',
  acknowledged: 'Bestätigt',
  in_progress: 'In Bearbeitung',
  partially_resolved: 'Teilweise erledigt',
  resolved: 'Erledigt',
  rejected: 'Abgelehnt',
  duplicate: 'Duplikat',
  forwarded: 'Weitergeleitet',
}

export const createdByKindLabels: Record<App.Enums.CreatedByKind, string> = {
  user: 'Nutzer',
  datafleet_sync: 'DataFleet Sync',
  flic: 'Flic',
  sap_sync: 'SAP Sync',
}

export const createdByKinds = [
  'user',
  'datafleet_sync',
  'flic',
  'sap_sync',
] as const satisfies readonly App.Enums.CreatedByKind[]

export const reporterUserTypes = [
  '000 – MP unbekannter User',
  '001 – Telefon',
  '002 – E-Mail',
  '003 – Fax',
  '004 – Anrufbeantworter',
  '005 – Brief',
  '006 – Besucher',
  '007 – Internet',
  '008 – Mängelmelder',
  '009 – MP interner User',
  '010 – MP externer User',
  '011 – DataFleet',
  '012 – Flic',
] as const satisfies readonly App.Enums.ReporterUserType[]

export const reporterUserTypeLabels: Record<App.Enums.ReporterUserType, string> = {
  '000 – MP unbekannter User': 'Unbekannter Nutzer',
  '001 – Telefon': 'Telefon',
  '002 – E-Mail': 'E-Mail',
  '003 – Fax': 'Fax',
  '004 – Anrufbeantworter': 'Anrufbeantworter',
  '005 – Brief': 'Brief',
  '006 – Besucher': 'Besucher',
  '007 – Internet': 'Internet',
  '008 – Mängelmelder': 'Mängelmelder',
  '009 – MP interner User': 'Interner Nutzer',
  '010 – MP externer User': 'Externer Nutzer',
  '011 – DataFleet': 'DataFleet',
  '012 – Flic': 'Flic',
}

export type IssueMelderLabelSource = {
  reporter?: { name?: string | null } | null
  reporter_type?: string | null
}

export type IssueCreatedByLabelSource = {
  created_by?: { name?: string | null } | null
  created_by_kind?: string | null
}

export function melderLabel(issue: IssueMelderLabelSource): string {
  if (issue.reporter?.name) {
    return issue.reporter.name
  }
  if (issue.reporter_type === 'App\\Models\\User') {
    return 'Nutzer'
  }
  if (issue.reporter_type === 'App\\Models\\Guest') {
    return 'Gast'
  }
  return '—'
}

function isCreatedByKind(value: string): value is App.Enums.CreatedByKind {
  return Object.hasOwn(createdByKindLabels, value)
}

export function createdByLabel(issue: IssueCreatedByLabelSource): string {
  if (issue.created_by?.name) {
    return issue.created_by.name
  }
  if (
    typeof issue.created_by_kind === 'string' &&
    issue.created_by_kind !== 'user' &&
    isCreatedByKind(issue.created_by_kind)
  ) {
    return createdByKindLabels[issue.created_by_kind]
  }
  return '—'
}

export const issueStatusColors: Record<App.Enums.IssueStatus, { fill: string; stroke: string }> = {
  reported: { fill: '#025093', stroke: '#025093' },
  acknowledged: { fill: '#6b21a8', stroke: '#6b21a8' },
  in_progress: { fill: '#ea580c', stroke: '#ea580c' },
  partially_resolved: { fill: '#65a30d', stroke: '#65a30d' }, // lime-600
  resolved: { fill: '#15803d', stroke: '#166534' },
  rejected: { fill: '#52525b', stroke: '#3f3f46' },
  duplicate: { fill: '#94a3b8', stroke: '#64748b' }, // slate-400
  forwarded: { fill: '#0ea5e9', stroke: '#0ea5e9' },
}

const ACTIVE_STATUSES = new Set<App.Enums.IssueStatus>([
  'acknowledged',
  'in_progress',
  'partially_resolved',
])
const TERMINAL_STATUSES = new Set<App.Enums.IssueStatus>(['resolved', 'rejected', 'duplicate'])
const RESOLVABLE_STATUSES = new Set<App.Enums.IssueStatus>([
  'reported',
  'acknowledged',
  'in_progress',
  'partially_resolved',
])
const ISSUE_PROCESSING_ACTION_TARGET_STATUSES: Partial<
  Record<App.Enums.IssueProcessingAction, App.Enums.IssueStatus>
> = {
  accept: 'acknowledged',
  reject: 'rejected',
  resolve: 'resolved',
}
const ISSUE_CLIPBOARD_NOTIFICATION_TITLES = {
  address: 'Adresse in Zwischenablage kopiert',
  email: 'E-Mail-Adresse in Zwischenablage kopiert',
  id: 'ID in Zwischenablage kopiert',
  url: 'Meldungs-URL in Zwischenablage kopiert',
} as const

export type IssueProcessable = {
  id: number
  issue_type_id?: number | null
  description?: string | null
}

export function isAssignableIssueType(
  type: { id: number; selectable_by_reporters: boolean },
  currentTypeId: number | null | undefined = null,
): boolean {
  return type.selectable_by_reporters || type.id === currentTypeId
}

export type IssueProcessFormData = {
  action: App.Enums.IssueProcessingAction | null
  issue_id: number | null
  issue_type_id: number | null
  description: string
  reject_reason: string | null
  source: string | null
}

export type IssueProcessPayload = IssueProcessFormData & {
  action: App.Enums.IssueProcessingAction
  issue_id: number
}

type IssueConfirmModalOptions = {
  type: 'info' | 'warning'
  positiveText: string
  negativeText: string
}

type IssuePromptModalOptions = IssueConfirmModalOptions & {
  center: boolean
  promptOptions: {
    required: boolean
    component: 'textarea'
    label: string
    hint: string
    placeholder: string
  }
}

export type ShowIssueConfirmModal = (
  title: string,
  message: string,
  options: IssueConfirmModalOptions,
) => Promise<boolean>

export type ShowIssuePromptModal = (
  title: string,
  message: string,
  options: IssuePromptModalOptions,
) => Promise<string | null>

export type IssueClipboardValueType = keyof typeof ISSUE_CLIPBOARD_NOTIFICATION_TITLES
export type CopyTextToClipboard = (text: string) => Promise<void>

export type IssueClipboardOptions = {
  isSupported?: boolean
  onCopied?: (notificationTitle: string) => void
}

export function isActiveStatus(status: App.Enums.IssueStatus): boolean {
  return ACTIVE_STATUSES.has(status)
}

export function isTerminalStatus(status: App.Enums.IssueStatus): boolean {
  return TERMINAL_STATUSES.has(status)
}

export function isResolvableStatus(status: App.Enums.IssueStatus): boolean {
  return RESOLVABLE_STATUSES.has(status)
}

export function getStatusLabel(status: App.Enums.IssueStatus): string {
  return issueStatusLabels[status]
}

export function getStatusColor(status: App.Enums.IssueStatus): {
  fill: string
  stroke: string
} {
  return issueStatusColors[status]
}

export function getIssueProcessingTargetStatus(
  action: App.Enums.IssueProcessingAction,
): App.Enums.IssueStatus | null {
  return ISSUE_PROCESSING_ACTION_TARGET_STATUSES[action] ?? null
}

export function getIssueClipboardNotificationTitle(type: IssueClipboardValueType): string {
  return ISSUE_CLIPBOARD_NOTIFICATION_TITLES[type]
}

export function createIssueProcessFormData(
  issue: IssueProcessable | null = null,
  overrides: Partial<IssueProcessFormData> = {},
): IssueProcessFormData {
  return {
    action: null,
    issue_id: issue?.id ?? null,
    issue_type_id: issue?.issue_type_id ?? null,
    description: issue?.description ?? '',
    reject_reason: null,
    source: null,
    ...overrides,
  }
}

export function createIssueProcessPayload(
  issue: IssueProcessable,
  action: App.Enums.IssueProcessingAction,
  overrides: Partial<Omit<IssueProcessFormData, 'action' | 'issue_id'>> = {},
): IssueProcessPayload {
  return {
    ...createIssueProcessFormData(issue, overrides),
    action,
    issue_id: issue.id,
  }
}

export async function promptIssueRejectReason(
  showPromptModal: ShowIssuePromptModal,
): Promise<string | null> {
  const reason = await showPromptModal(
    'Meldung ablehnen',
    'Bitte gib einen kurzen Grund für die Ablehnung ein.',
    {
      type: 'warning',
      center: true,
      positiveText: 'Ablehnen',
      negativeText: 'Abbrechen',
      promptOptions: {
        required: true,
        component: 'textarea',
        label: 'Begründung',
        hint: 'z. B. doppelte Meldung, nicht zuständig, unvollständig…',
        placeholder: 'Begründung eingeben…',
      },
    },
  )

  if (reason === null) return null

  const trimmedReason = reason.trim()
  return trimmedReason.length >= 3 ? trimmedReason : null
}

export function confirmIssueResolution(showConfirmModal: ShowIssueConfirmModal): Promise<boolean> {
  return showConfirmModal('Meldung als erledigt markieren?', '', {
    type: 'info',
    positiveText: 'Als erledigt markieren',
    negativeText: 'Abbrechen',
  })
}

export function getIssueProUrl(
  issueId: number,
  origin: string = globalThis.location.origin,
): string {
  return `${origin.replace(/\/$/, '')}/admin/issues/${issueId}`
}

export async function copyIssueValueToClipboard(
  copy: CopyTextToClipboard,
  type: IssueClipboardValueType,
  value: string | number | null | undefined,
  options: IssueClipboardOptions = {},
): Promise<boolean> {
  if (options.isSupported === false) return false
  if (value === null || value === undefined) return false

  const text = String(value)
  if (text.length === 0) return false

  await copy(text)
  options.onCopied?.(getIssueClipboardNotificationTitle(type))

  return true
}

export function openIssuePro(issueId: number, origin?: string): Window | null {
  return openUrlInNewWindow(getIssueProUrl(issueId, origin))
}

export function getIssueLocationMatchUrl(
  issueId: number,
  origin: string = globalThis.location.origin,
): string {
  return `${origin.replace(/\/$/, '')}/admin/issues/${issueId}/location-match`
}

export function openIssueLocationMatch(issueId: number, origin?: string): Window | null {
  return openUrlInNewWindow(getIssueLocationMatchUrl(issueId, origin))
}

export function getIssueGoogleMapsUrl(coordinates: readonly [number, number]): string {
  const [lng, lat] = coordinates
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
}

export function openIssueInGoogleMaps(coordinates: readonly [number, number]): Window | null {
  return openUrlInNewWindow(getIssueGoogleMapsUrl(coordinates))
}
